import React, { useState } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileCode } from '@fortawesome/pro-regular-svg-icons/faFileCode'
import { faUpload } from '@fortawesome/pro-regular-svg-icons/faUpload'

import { Checkbox, Form, Modal } from '@peracto/peracto-ui'
import { useConfig } from '@peracto/peracto-config'

import ContentImportResult from './ContentImportResult'

const ContentImportButton = ({ type }) => {
    const config = useConfig()
    const API_URL = config.get('api')
    const { content: contentFeatures } = config.get('features', {})

    const [isVisible, setIsVisible] = useState(false)
    const [file, setFile] = useState()
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)
    const [errors, setErrors] = useState([])

    const reset = () => {
        setFile()
        setResult(null)
        setErrors([])
    }

    const close = () => {
        setIsVisible(false)
        reset()
    }

    const handleSubmit = async ({
        overrideResources,
        linkProducts,
        linkArticleCategories,
        updateAccessRestrictions,
    }) => {
        if (!file) {
            return
        }

        setLoading(true)
        setErrors([])

        try {
            const exportData = JSON.parse(await file.text())

            const { data } = await axios.post(
                `${API_URL}/contents/import`,
                {
                    overrideResources,
                    linkProducts,
                    linkArticleCategories,
                    updateAccessRestrictions,
                    export: exportData,
                },
                {
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                        ...(contentFeatures?.indexContent ? { 'peracto-index-content': true } : {}),
                    },
                }
            )

            setResult(data)
            toast.success('Content imported')
        } catch (e) {
            const response = e?.response?.data

            if (response?.violations) {
                setErrors(response.violations.map(v => `${v.propertyPath}: ${v.message}`))
                return
            }

            setErrors([response?.detail || e.message || 'Failed to import content'])
        } finally {
            setLoading(false)
        }
    }

    const buttons = [
        { type: 'btn-outline-secondary', text: 'Close', action: close },
        ...(result ? [{ type: 'btn-primary', text: 'Import Another', action: reset }] : []),
    ]

    return (
        <>
            <button
                type="button"
                className="btn btn-outline-primary !mr-2"
                onClick={() => setIsVisible(true)}
                data-testid="import-content"
            >
                <FontAwesomeIcon icon={faUpload} className="!mr-2" />
                Import
            </button>

            <Modal
                title="Import Content"
                isVisible={isVisible}
                close={close}
                bodyClassName={result ? '' : '!items-stretch !text-left'}
                buttons={buttons}
            >
                {isVisible && !result && (
                    <Form
                        values={{
                            overrideResources: false,
                            linkProducts: false,
                            linkArticleCategories: false,
                            updateAccessRestrictions: false,
                        }}
                        showActionPanel={false}
                        enableReinitialize={false}
                        onSubmit={handleSubmit}
                    >
                        <label className="!block !mb-2">Which export file are you importing?</label>
                        <div className="!mb-4">
                            <div className="!relative !overflow-hidden !inline-block">
                                <input
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        opacity: 0,
                                        cursor: 'pointer',
                                    }}
                                    type="file"
                                    accept="application/json,.json"
                                    data-testid="import-content-file"
                                    onChange={e => setFile(e.currentTarget.files?.[0])}
                                />
                                <div className="!flex !items-center">
                                    <button type="button" className="btn btn-outline-primary !mr-2">
                                        <FontAwesomeIcon icon={faFileCode} className="!mr-2" />
                                        Choose File
                                    </button>
                                    <span>{file?.name || 'No File Selected'}</span>
                                </div>
                            </div>
                        </div>

                        <label className="!block">Which relations should be imported?</label>
                        <Checkbox
                            name="linkProducts"
                            label="Import product links"
                            help="Replaces the existing product links with those in the file. Products that don't exist on this site are skipped and listed after the import."
                        />
                        <Checkbox
                            name="linkArticleCategories"
                            label="Import article categories"
                            help="Replaces the existing article categories with those in the file. Categories that don't exist on this site are skipped and listed after the import."
                        />
                        <Checkbox
                            name="updateAccessRestrictions"
                            label="Import access restrictions"
                            help="Replaces the existing access restrictions with those in the file. If the file has none, the content will be visible to everyone. If a customer group or account in the file doesn't exist on this site, only admins will be able to see the content."
                        />

                        <label className="!block">How should existing assets be treated?</label>
                        <Checkbox
                            name="overrideResources"
                            label="Overwrite assets that already exist"
                            help="Replaces assets that already exist on this site with the ones in the file. New assets are always copied."
                        />

                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!file || loading}
                            data-testid="import-content-submit"
                        >
                            {loading ? 'Importing...' : 'Import Content'}
                        </button>
                    </Form>
                )}

                {errors.length > 0 && (
                    <div className="alert alert-danger !mt-2 !mb-0">
                        {errors.map(error => (
                            <div key={error}>{error}</div>
                        ))}
                    </div>
                )}

                {result && <ContentImportResult result={result} type={type} />}
            </Modal>
        </>
    )
}

export default ContentImportButton
